/**
 * AdminSidebar — Merchant Sidebar'ın birebir yapı kopyası.
 *
 * Section header'ları, NavLink pattern'i, aktif-item şeridi, alt
 * user dropdown; her detay merchant Sidebar.jsx ile hizalı. Tek fark
 * nav items farklı (admin modülleri) ve dropdown admin logout akışı
 * kullanır.
 *
 * Admin kapsamında (`data-admin-scope`) `--primary` siyah olduğu için
 * aktif chip + hover tonları otomatik siyah hale gelir — ekstra override
 * gerekmez.
 */
import { NavLink, useNavigate } from "react-router-dom";
import {
    Users,
    ChevronUp, LogOut,
} from "lucide-react";
import { useAdminAuth } from "@/lib/admin-auth";
import { Logo } from "@/components/brand/Logo";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ADMIN_LOGIN_PATH, ADMIN_DASHBOARD_PATH } from "@/lib/admin-path";

const groups = [
    {
        section: "Panel",
        items: [
            { to: ADMIN_DASHBOARD_PATH, end: true, label: "Hesaplar", icon: Users, testId: "admin-nav-tenants" },
        ],
    },
];

const initials = (email = "") => {
    const local = email.split("@")[0] || "";
    return local.slice(0, 2).toUpperCase() || "SA";
};

export default function AdminSidebar() {
    const { admin, logout } = useAdminAuth();
    const nav = useNavigate();

    const handleLogout = async () => {
        await logout();
        nav(ADMIN_LOGIN_PATH, { replace: true });
    };

    return (
        <aside
            className="hidden lg:flex w-[252px] shrink-0 flex-col border-r border-border bg-background sticky top-0 h-screen"
            data-testid="admin-sidebar"
        >
            <div className="h-16 flex items-center justify-between pl-5 pr-5 border-b border-border">
                <Logo />
            </div>

            <nav className="flex-1 overflow-y-auto scroll-thin px-3 py-5 space-y-6">
                {groups.map((g) => (
                    <Section key={g.section} label={g.section}>
                        {g.items.map((it) => (
                            <NavItem key={it.to} {...it} />
                        ))}
                    </Section>
                ))}
            </nav>

            {/* Kullanıcı ayak bloğu — merchant Sidebar ile aynı tasarım. */}
            <div className="shrink-0 border-t border-border px-3 py-3" data-testid="admin-sidebar-footer">
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <button
                            className="w-full flex items-center gap-2.5 px-2 py-2 rounded-md hover:bg-muted/60 transition-colors group"
                            data-testid="admin-user-menu"
                        >
                            <Avatar className="h-8 w-8 border border-border shrink-0">
                                <AvatarFallback className="bg-foreground text-background text-[11px] font-semibold">
                                    {initials(admin?.email)}
                                </AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0 text-left leading-tight">
                                <div className="text-[12.5px] font-medium truncate" data-testid="admin-user-email">
                                    {admin?.email || "—"}
                                </div>
                                <div className="text-[10.5px] text-muted-foreground truncate">
                                    Süper Admin
                                </div>
                            </div>
                            <ChevronUp className="h-3.5 w-3.5 text-muted-foreground opacity-60 group-hover:opacity-100 transition-opacity shrink-0" strokeWidth={2} />
                        </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent side="top" align="start" className="w-[228px]">
                        <DropdownMenuLabel className="font-normal">
                            <div className="flex flex-col leading-tight">
                                <span className="text-sm font-medium">{admin?.email}</span>
                                <span className="text-[10px] text-muted-foreground mt-1">
                                    Süper Admin · Sayz
                                </span>
                            </div>
                        </DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={handleLogout} className="cursor-pointer" data-testid="admin-logout-btn">
                            <LogOut className="h-3.5 w-3.5 mr-2" /> Çıkış yap
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
        </aside>
    );
}

const Section = ({ label, children }) => (
    <div>
        <div className="px-2 mb-2 text-[10px] font-semibold tracking-[0.14em] uppercase text-muted-foreground">
            {label}
        </div>
        <div className="space-y-0.5">{children}</div>
    </div>
);

const NavItem = ({ to, end, icon: Icon, label, testId }) => (
    <NavLink
        to={to}
        end={end}
        data-testid={testId}
        className={({ isActive }) =>
            [
                "group relative flex items-center gap-3 rounded-md px-2.5 py-2 text-[13px] transition-colors",
                isActive
                    ? "bg-muted text-foreground font-medium"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted/60",
            ].join(" ")
        }
    >
        {({ isActive }) => (
            <>
                <span
                    aria-hidden
                    className={`absolute left-0 top-1.5 bottom-1.5 w-[2px] rounded-r-sm ${isActive ? "bg-foreground" : "bg-transparent"}`}
                />
                <Icon className="h-4 w-4 shrink-0" strokeWidth={1.75} />
                <span>{label}</span>
            </>
        )}
    </NavLink>
);
